import { useEffect, useMemo, useState } from "react";
import { api, errMsg, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Car, Plus, FileDown, Copy, Pencil, Trash2, Sparkles, Eye, X, ClipboardCopy, BarChart3 } from "lucide-react";

const SEVERITIES = [
  { key: "leves", label: "LEVES", initial: 10, color: "bg-amber-100 text-amber-800" },
  { key: "deficientes", label: "GRAVES / DEFICIENTES", initial: 2, color: "bg-orange-100 text-orange-800" },
  { key: "eliminatorias", label: "ELIMINATORIAS", initial: 1, color: "bg-red-100 text-red-700" },
];

const emptyFaults = () => ({ leves: Array(10).fill(""), deficientes: Array(2).fill(""), eliminatorias: Array(1).fill("") });

async function downloadPdf(exam) {
  try {
    const r = await api.get(`/practical-exams/${exam.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `practico_${exam.exam_number}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error("No se pudo generar el PDF"); }
}

export default function PracticalExams() {
  const { user } = useAuth();
  return (
    <div className="fade-up" data-testid="practical-exams-page">
      <h1 className="text-3xl font-semibold tracking-tight mb-1 flex items-center gap-2"><Car className="text-orange-600" /> Exámenes Prácticos</h1>
      <p className="text-neutral-500 mb-6">
        {user.role === "ALUMNO" ? "Historial de tus exámenes prácticos, validados por tu profesor." : "Registro y corrección de exámenes prácticos DGT. Dakota sugiere, el profesor decide."}
      </p>
      {user.role === "ALUMNO" ? <StudentExams /> : <StaffExams />}
    </div>
  );
}

function ResultBadge({ result }) {
  const ok = result === "APTO";
  return <span data-testid="exam-result-badge" className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${ok ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{ok ? "APTO" : "NO APTO"}</span>;
}

function StudentExams() {
  const [exams, setExams] = useState(null);
  useEffect(() => { api.get("/practical-exams").then((r) => setExams(r.data)); }, []);
  if (!exams) return <p className="text-neutral-500">Cargando…</p>;
  return (
    <div className="space-y-4">
      {exams.map((e) => (
        <div key={e.id} className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6" data-testid="student-exam-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold">Práctico #{e.exam_number} <span className="text-neutral-400 font-normal">· {fmtDate(e.date)}{e.exam_center ? ` · ${e.exam_center}` : ""}</span></p>
            </div>
            <div className="flex items-center gap-2">
              <ResultBadge result={e.result} />
              <button data-testid="student-exam-pdf-btn" onClick={() => downloadPdf(e)} className="p-1.5 text-neutral-400 hover:text-orange-600" title="Descargar PDF"><FileDown size={16} /></button>
            </div>
          </div>
          {e.final_report && (
            <div className="mt-3 bg-neutral-50 rounded-xl p-4 text-sm whitespace-pre-wrap" data-testid="student-exam-report">{e.final_report}</div>
          )}
          {e.validated_by && <p className="mt-2 text-xs text-neutral-500">Validado por: <strong>{e.validated_by}</strong></p>}
        </div>
      ))}
      {exams.length === 0 && <p className="text-sm text-neutral-500 bg-white border border-neutral-200/70 rounded-2xl p-6">Todavía no tienes exámenes prácticos registrados.</p>}
    </div>
  );
}

function StaffExams() {
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [exams, setExams] = useState([]);
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | exam object
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    api.get("/users").then((r) => setStudents(r.data.filter((u) => u.role === "ALUMNO")));
    api.get("/dgt-codes").then((r) => setCodes(r.data)).catch(() => {});
  }, []);
  const load = () => {
    api.get(`/practical-exams${studentId ? `?student_id=${studentId}` : ""}`).then((r) => setExams(r.data));
    api.get(`/practical-exams/stats${studentId ? `?student_id=${studentId}` : ""}`).then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [studentId]);

  const student = students.find((s) => s.id === studentId);

  const duplicate = async (e) => {
    try { await api.post(`/practical-exams/${e.id}/duplicate`); toast.success("Examen duplicado"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (e) => {
    const pin = window.prompt(`PIN Maestro para ELIMINAR el Práctico #${e.exam_number} de ${e.student_name}:`);
    if (!pin) return;
    try { await api.delete(`/practical-exams/${e.id}`, { headers: { "X-Master-Pin": pin } }); toast.success("Examen eliminado (borrado suave, queda auditado)"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const copyReport = async (e) => {
    const faults = e.faults || {};
    const txt = `NATUR AUTOESCUELA — Informe de Examen Práctico\nAlumno: ${e.student_name}\nPráctico #${e.exam_number} · ${e.date} · ${e.exam_center || "—"}\nResultado: ${e.result === "APTO" ? "APTO" : "NO APTO"}\nLEVES: ${(faults.leves || []).join(", ") || "—"}\nGRAVES/DEFICIENTES: ${(faults.deficientes || []).join(", ") || "—"}\nELIMINATORIAS: ${(faults.eliminatorias || []).join(", ") || "—"}\n\nInforme del profesor:\n${e.final_report || "Sin informe"}\n\nValidado por: ${e.validated_by || ""}`;
    try { await navigator.clipboard.writeText(txt); toast.success("Informe copiado al portapapeles"); }
    catch { toast.error("No se pudo copiar"); }
  };

  return (
    <div className="space-y-6">
      <datalist id="dgt-codes-list">
        {codes.map((c) => <option key={c.code} value={c.code} label={`${c.code} — ${c.description}`} />)}
      </datalist>
      <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6 flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-xs text-neutral-500 block mb-1">Alumno</label>
          <select data-testid="exam-student-select" value={studentId} onChange={(e) => { setStudentId(e.target.value); setEditing(null); }}
                  className="h-10 rounded-md border border-neutral-300 px-3 text-sm w-64">
            <option value="">Todos mis alumnos…</option>
            {students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <Button data-testid="exam-new-btn" disabled={!studentId} onClick={() => setEditing("new")}
                className="rounded-full bg-orange-600 hover:bg-orange-700 text-white">
          <Plus size={15} className="mr-1" /> Nuevo examen práctico
        </Button>
        {!studentId && <p className="text-xs text-neutral-400">Selecciona un alumno para registrar un examen.</p>}
      </div>

      {codes.length === 0 && (
        <div data-testid="exam-empty-codes-warning" className="bg-red-50 border border-red-200 rounded-2xl p-4 text-sm text-red-800">
          ⚠️ El desplegable de claves DGT está vacío. Sube el PDF oficial en <strong>Gestión → Conocimiento Dakota</strong>, márcalo como <strong>"Criterios de Calificación DGT"</strong>, apruébalo y pulsa <strong>🧠 Inyectar conocimiento</strong>. Solo esas claves se usarán en los desplegables.
        </div>
      )}

      {editing && (
        <ExamForm student={student} exam={editing === "new" ? null : editing} codes={codes}
                  onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />
      )}

      <StatsPanel stats={stats} perStudent={!!studentId} />

      <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm divide-y divide-neutral-100">
        <p className="p-4 text-sm font-semibold">{studentId ? `Historial de ${student?.name || "alumno"}` : "Exámenes prácticos registrados"}</p>
        {exams.map((e) => (
          <div key={e.id} className="p-4" data-testid="exam-row">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium">Práctico #{e.exam_number} · {e.student_name}
                  <span className="text-neutral-400 font-normal"> · {fmtDate(e.date)}{e.exam_center ? ` · ${e.exam_center}` : ""}</span>
                </p>
                <p className="text-xs text-neutral-500 mt-0.5">
                  Leves: {(e.faults?.leves || []).length} · Deficientes: {(e.faults?.deficientes || []).length} · Eliminatorias: {(e.faults?.eliminatorias || []).length}
                  {e.validated_by ? ` · Validado por ${e.validated_by}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                <ResultBadge result={e.result} />
                <button data-testid="exam-view-btn" title="Ver" onClick={() => setViewing(viewing?.id === e.id ? null : e)} className="p-1.5 text-neutral-400 hover:text-neutral-800"><Eye size={15} /></button>
                <button data-testid="exam-edit-btn" title="Editar" onClick={() => setEditing(e)} className="p-1.5 text-neutral-400 hover:text-orange-600"><Pencil size={15} /></button>
                <button data-testid="exam-duplicate-btn" title="Duplicar" onClick={() => duplicate(e)} className="p-1.5 text-neutral-400 hover:text-blue-600"><Copy size={15} /></button>
                <button data-testid="exam-copy-btn" title="Copiar informe" onClick={() => copyReport(e)} className="p-1.5 text-neutral-400 hover:text-green-700"><ClipboardCopy size={15} /></button>
                <button data-testid="exam-pdf-btn" title="PDF" onClick={() => downloadPdf(e)} className="p-1.5 text-neutral-400 hover:text-orange-600"><FileDown size={15} /></button>
                <button data-testid="exam-delete-btn" title="Eliminar (PIN)" onClick={() => del(e)} className="p-1.5 text-neutral-400 hover:text-red-600"><Trash2 size={15} /></button>
              </div>
            </div>
            {viewing?.id === e.id && (
              <div className="mt-3 bg-neutral-50 rounded-xl p-4 text-sm space-y-3" data-testid="exam-detail">
                {SEVERITIES.map((s) => (
                  <p key={s.key}><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full mr-2 ${s.color}`}>{s.label} ({(e.faults?.[s.key] || []).length})</span>{(e.faults?.[s.key] || []).join(", ") || "—"}</p>
                ))}
                {e.examiner_observations && <div><p className="text-xs font-semibold text-neutral-500 mb-1">Observaciones del examinador</p><p className="whitespace-pre-wrap">{e.examiner_observations}</p></div>}
                {e.dakota_suggestion && <div><p className="text-xs font-semibold text-orange-700 mb-1">Sugerencia de Dakota (interna, no visible para el alumno)</p><p className="whitespace-pre-wrap text-neutral-600">{e.dakota_suggestion}</p></div>}
                <div><p className="text-xs font-semibold text-neutral-500 mb-1">Informe final del profesor (visible para el alumno)</p><p className="whitespace-pre-wrap">{e.final_report || "Sin informe"}</p></div>
                {e.history?.length > 0 && (
                  <p className="text-[11px] text-neutral-400">Auditoría: {e.history.map((h) => `${h.action} por ${h.by} (${fmtDate(h.at)})`).join(" · ")}</p>
                )}
              </div>
            )}
          </div>
        ))}
        {exams.length === 0 && <p className="p-4 text-sm text-neutral-500">Sin exámenes registrados.</p>}
      </div>
    </div>
  );
}

function StatsPanel({ stats, perStudent }) {
  if (!stats) return null;
  if (perStudent) {
    if (!stats.exams) return null;
    const max = Math.max(...stats.codes.map((c) => c.count), 1);
    return (
      <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6" data-testid="exam-stats-panel">
        <h3 className="font-semibold flex items-center gap-2 mb-1"><BarChart3 size={17} className="text-orange-600" /> Estadísticas de faltas — {stats.student?.name}</h3>
        <p className="text-xs text-neutral-500 mb-4">{stats.exams} exámenes · <span className="text-green-700 font-semibold">{stats.aptos} APTO</span> · <span className="text-red-600 font-semibold">{stats.no_aptos} NO APTO</span>. Claves más repetidas: enfoca las clases donde más falla.</p>
        {stats.codes.length === 0 ? <p className="text-sm text-neutral-500">Sin faltas registradas.</p> : (
          <div className="space-y-2">
            {stats.codes.slice(0, 10).map((c) => (
              <div key={c.code} data-testid="exam-stats-code-row">
                <div className="flex items-center justify-between text-sm">
                  <p><span className="font-semibold text-orange-700">{c.code}</span>{c.description ? <span className="text-neutral-500"> — {c.description}</span> : ""}</p>
                  <p className="text-xs text-neutral-500 shrink-0 ml-3">
                    ×{c.count}
                    {c.leves > 0 && <span className="ml-2 text-amber-700">L:{c.leves}</span>}
                    {c.deficientes > 0 && <span className="ml-2 text-orange-700">D:{c.deficientes}</span>}
                    {c.eliminatorias > 0 && <span className="ml-2 text-red-600">E:{c.eliminatorias}</span>}
                  </p>
                </div>
                <div className="h-2 bg-neutral-100 rounded-full mt-1">
                  <div className="h-2 rounded-full bg-gradient-to-r from-orange-500 to-orange-600" style={{ width: `${(c.count / max) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
  if (!stats.students?.length) return null;
  return (
    <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6" data-testid="exam-stats-overview">
      <h3 className="font-semibold flex items-center gap-2 mb-3"><BarChart3 size={17} className="text-orange-600" /> Resumen de faltas por alumno</h3>
      <div className="divide-y divide-neutral-100">
        {stats.students.map((s) => (
          <div key={s.student_id} className="py-2.5 flex flex-wrap items-center justify-between gap-2" data-testid="exam-stats-student-row">
            <p className="text-sm font-medium">{s.student_name} <span className="text-xs text-neutral-400 font-normal">· {s.exams} exámenes · {s.aptos} APTO / {s.no_aptos} NO APTO</span></p>
            <div className="flex flex-wrap gap-1.5">
              {s.top_codes.map((c) => (
                <span key={c.code} title={c.description || ""} className="text-[11px] font-semibold bg-orange-50 text-orange-800 border border-orange-200 px-2 py-0.5 rounded-full">{c.code} ×{c.count}</span>
              ))}
              {s.top_codes.length === 0 && <span className="text-[11px] text-neutral-400">Sin faltas</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FaultBlock({ sev, values, codes, onChange, cards, setCard }) {
  const filled = values.filter((v) => v.trim()).length;
  return (
    <div className="border border-neutral-200 rounded-xl p-4" data-testid={`fault-block-${sev.key}`}>
      <p className="text-xs font-bold mb-3"><span className={`px-2 py-0.5 rounded-full ${sev.color}`}>{sev.label} ({filled})</span></p>
      <div className="space-y-3">
        {values.map((v, i) => {
          const card = v.trim() ? cards[v.trim()] : null;
          const causes = card ? (card[sev.key] || []) : [];
          return (
            <div key={i} className="bg-neutral-50 rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2">
                <input list="dgt-codes-list" value={v} data-testid={`fault-input-${sev.key}-${i}`}
                       onChange={(e) => onChange(values.map((x, j) => (j === i ? e.target.value : x)))}
                       onBlur={(e) => { const c = e.target.value.trim(); if (c) setCard(c); }}
                       placeholder="Clave DGT (ej. 1.1)"
                       className="h-9 w-32 rounded-md border border-neutral-300 px-2.5 text-sm font-semibold" />
                {card && (
                  <p className="text-sm text-neutral-700 flex-1 truncate">
                    <span className="text-neutral-500">·</span> {card.categoria || "—"} <span className="text-neutral-400">·</span> <span className="font-medium">{card.epigrafe || "—"}</span>
                  </p>
                )}
                <button type="button" onClick={() => onChange(values.filter((_, j) => j !== i))}
                        className="p-1 text-neutral-300 hover:text-red-500 shrink-0" title="Quitar clave"><X size={14} /></button>
              </div>
              {card && (
                <>
                  {causes.length > 0 ? (
                    <div>
                      <p className="text-[11px] text-neutral-500 mb-1">Posibles causas · marca las que aplican</p>
                      <div className="grid md:grid-cols-2 gap-1">
                        {causes.map((cause, ci) => {
                          const checked = (card._checked || []).includes(cause);
                          return (
                            <label key={ci} className="flex items-start gap-2 text-sm cursor-pointer hover:bg-white px-1.5 py-0.5 rounded"
                                   data-testid={`fault-cause-${sev.key}-${i}-${ci}`}>
                              <input type="checkbox" checked={checked} className="mt-0.5"
                                     onChange={() => {
                                       const list = card._checked || [];
                                       const next = checked ? list.filter((x) => x !== cause) : [...list, cause];
                                       setCard(v.trim(), { ...card, _checked: next });
                                     }} />
                              <span className={checked ? "font-medium text-neutral-800" : "text-neutral-600"}>{cause}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="text-[11px] text-neutral-400">Sin causas listadas para esta categoría en la ficha oficial.</p>
                  )}
                  {card.notas?.length > 0 && (
                    <p className="text-[11px] text-neutral-500 bg-white rounded px-2 py-1 border border-neutral-200">
                      <strong>Nota:</strong> {card.notas.join(" · ")}
                    </p>
                  )}
                </>
              )}
              {v.trim() && card && !card.found && (
                <p className="text-[11px] text-amber-700">Clave registrada. Puedes anotar la causa a mano en las observaciones.</p>
              )}
            </div>
          );
        })}
      </div>
      <button type="button" data-testid={`fault-add-${sev.key}`} onClick={() => onChange([...values, ""])}
              className="mt-3 text-xs text-orange-700 hover:text-orange-800 font-semibold flex items-center gap-1">
        <Plus size={12} /> Añadir otra clave
      </button>
    </div>
  );
}

function ExamForm({ student, exam, codes, onSaved, onCancel }) {
  const [form, setForm] = useState(() => exam ? {
    date: exam.date || "", exam_center: exam.exam_center || "", result: exam.result || "NO_APTO",
    examiner_observations: exam.examiner_observations || "", dakota_suggestion: exam.dakota_suggestion || "",
    final_report: exam.final_report || "",
    faults: { leves: [...(exam.faults?.leves || []), ""], deficientes: [...(exam.faults?.deficientes || []), ""], eliminatorias: [...(exam.faults?.eliminatorias || []), ""] },
  } : { date: new Date().toISOString().slice(0, 10), exam_center: "", result: "NO_APTO", examiner_observations: "", dakota_suggestion: "", final_report: "", faults: emptyFaults() });
  const [cards, setCards] = useState({}); // code -> card {categoria, epigrafe, ..., _checked:[]}
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [centros, setCentros] = useState([]);
  const [resultados, setResultados] = useState([]);
  const [plantillas, setPlantillas] = useState([]);

  useEffect(() => {
    api.get("/lov/CENTROS_EXAMEN_DGT").then((r) => setCentros(r.data.values || [])).catch(() => {});
    api.get("/lov/RESULTADOS_EXAMEN").then((r) => setResultados(r.data.values || [])).catch(() => {});
    api.get("/lov/PLANTILLAS_OBSERVACION").then((r) => setPlantillas(r.data.values || [])).catch(() => {});
  }, []);

  const cleanFaults = useMemo(() => ({
    leves: form.faults.leves.map((c) => c.trim()).filter(Boolean),
    deficientes: form.faults.deficientes.map((c) => c.trim()).filter(Boolean),
    eliminatorias: form.faults.eliminatorias.map((c) => c.trim()).filter(Boolean),
  }), [form.faults]);

  const setCard = async (code, override) => {
    if (override) { setCards((prev) => ({ ...prev, [code]: override })); return; }
    if (cards[code]) return;
    try {
      const r = await api.get(`/dgt-codes/${encodeURIComponent(code)}/card`);
      setCards((prev) => ({ ...prev, [code]: { ...r.data, _checked: [] } }));
    } catch { /* silently ignore, teacher puede anotar manualmente */ }
  };

  const buildDraft = () => {
    // Compone el borrador leyendo lo que el profesor ha marcado, sin jerga técnica.
    const parts = [];
    SEVERITIES.forEach((sev) => {
      const codesSev = cleanFaults[sev.key];
      if (!codesSev.length) return;
      const lines = [];
      codesSev.forEach((code) => {
        const card = cards[code];
        if (!card) { lines.push(`- ${code}`); return; }
        const head = `${code} · ${card.epigrafe || card.categoria || ""}`.replace(/·\s*$/, "").trim();
        const checked = card._checked || [];
        if (checked.length) {
          lines.push(`- ${head}: ${checked.join("; ")}`);
        } else {
          lines.push(`- ${head}`);
        }
        if (card.notas?.length) {
          lines.push(`  · Nota: ${card.notas.join(" · ")}`);
        }
      });
      parts.push(`${sev.label}:\n${lines.join("\n")}`);
    });
    return parts.join("\n\n");
  };

  const composeDraft = () => {
    setDrafting(true);
    try {
      const draft = buildDraft() || "Sin faltas registradas.";
      const intro = form.result === "APTO"
        ? "Enhorabuena por el resultado APTO. Puntos a reforzar para asentar la conducción:"
        : "Resultado NO APTO. Puntos concretos a trabajar antes de la próxima convocatoria:";
      const closing = "\n\nPlan de refuerzo sugerido: repasa los apartados marcados en la próxima clase práctica y confirma su interiorización antes del siguiente examen.";
      setForm((f) => ({ ...f, final_report: `${intro}\n\n${draft}${closing}` }));
      toast.success("Borrador generado. Edita a tu gusto antes de guardar.");
    } finally { setDrafting(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { date: form.date, result: form.result, exam_center: form.exam_center, faults: cleanFaults,
                     examiner_observations: form.examiner_observations, dakota_suggestion: form.dakota_suggestion,
                     final_report: form.final_report };
      if (exam) await api.patch(`/practical-exams/${exam.id}`, body);
      else await api.post("/practical-exams", { ...body, student_id: student.id });
      toast.success(exam ? "Examen actualizado" : "Examen registrado");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  // Pre-carga fichas al abrir edición
  useEffect(() => {
    const all = [...cleanFaults.leves, ...cleanFaults.deficientes, ...cleanFaults.eliminatorias];
    all.forEach((c) => setCard(c));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bg-white border-2 border-orange-200 rounded-2xl shadow-sm p-6 space-y-4" data-testid="exam-form">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">{exam ? `Editar Práctico #${exam.exam_number} — ${exam.student_name}` : `Nuevo examen práctico — ${student?.name}`}</h3>
        <button onClick={onCancel} data-testid="exam-form-cancel" className="text-neutral-400 hover:text-neutral-700"><X size={18} /></button>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <div>
          <label className="text-xs text-neutral-500 block mb-1">Fecha del examen</label>
          <Input data-testid="exam-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        </div>
        <div>
          <label className="text-xs text-neutral-500 block mb-1">Centro de examen</label>
          <div className="flex gap-2">
            <select data-testid="exam-center-select" value={form.exam_center}
                    onChange={(e) => setForm({ ...form, exam_center: e.target.value })}
                    className="h-10 flex-1 rounded-md border border-neutral-300 px-3 text-sm bg-white">
              <option value="">— Selecciona —</option>
              {centros.map((c) => <option key={c.id} value={c.label}>{c.label}</option>)}
            </select>
            <button type="button" title="Añadir centro" data-testid="exam-center-add-btn"
                    onClick={async () => {
                      const label = window.prompt("Nuevo centro de examen:"); if (!label) return;
                      const pin = window.prompt("PIN Maestro:"); if (!pin) return;
                      try {
                        await api.post("/lov/CENTROS_EXAMEN_DGT", { label }, { headers: { "X-Master-Pin": pin } });
                        const r = await api.get("/lov/CENTROS_EXAMEN_DGT");
                        setCentros(r.data.values || []);
                        setForm((f) => ({ ...f, exam_center: label }));
                        toast.success("Centro añadido al catálogo");
                      } catch (e) { toast.error(errMsg(e)); }
                    }}
                    className="h-10 px-3 rounded-md border border-neutral-300 text-neutral-500 hover:text-orange-600 hover:border-orange-300">+</button>
          </div>
        </div>
        <div>
          <label className="text-xs text-neutral-500 block mb-1">Resultado</label>
          <select data-testid="exam-result-select" value={form.result} onChange={(e) => setForm({ ...form, result: e.target.value })}
                  className="h-10 w-full rounded-md border border-neutral-300 px-3 text-sm">
            {resultados.length > 0
              ? resultados.map((r) => {
                  const val = r.label.replace(/\s+/g, "_").toUpperCase();
                  return <option key={r.id} value={val}>{r.label}</option>;
                })
              : <><option value="NO_APTO">NO APTO</option><option value="APTO">APTO</option></>
            }
          </select>
        </div>
      </div>
      <div className="space-y-3">
        {SEVERITIES.map((sev) => (
          <FaultBlock key={sev.key} sev={sev} codes={codes} values={form.faults[sev.key]} cards={cards} setCard={setCard}
                      onChange={(vals) => setForm({ ...form, faults: { ...form.faults, [sev.key]: vals } })} />
        ))}
      </div>
      <div>
        <label className="text-xs text-neutral-500 block mb-1">Observaciones (opcional)</label>
        {plantillas.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-1.5">
            <span className="text-[10px] uppercase text-neutral-400 self-center">Plantillas:</span>
            {plantillas.map((p) => (
              <button key={p.id} type="button" data-testid="exam-observation-template"
                      onClick={() => setForm((f) => ({ ...f, examiner_observations: f.examiner_observations ? `${f.examiner_observations}\n${p.label}` : p.label }))}
                      className="text-[11px] px-2 py-0.5 rounded-full bg-neutral-100 hover:bg-orange-100 text-neutral-600 hover:text-orange-700 border border-neutral-200 truncate max-w-xs" title={p.label}>
                {p.label.length > 40 ? p.label.slice(0, 40) + "…" : p.label}
              </button>
            ))}
          </div>
        )}
        <Textarea data-testid="exam-observations-input" rows={2} value={form.examiner_observations}
                  onChange={(e) => setForm({ ...form, examiner_observations: e.target.value })}
                  placeholder="Anotaciones adicionales…" />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button data-testid="exam-draft-btn" onClick={composeDraft} disabled={drafting} variant="outline"
                className="rounded-full border-orange-300 text-orange-700 hover:bg-orange-50">
          <Sparkles size={15} className="mr-1.5" /> {drafting ? "Generando…" : "Generar borrador"}
        </Button>
        <p className="text-xs text-neutral-400">Genera un texto listo para editar a partir de las causas marcadas.</p>
      </div>
      <div>
        <label className="text-xs text-neutral-500 block mb-1">Informe final del profesor (esto es lo que verá el alumno)</label>
        <Textarea data-testid="exam-report-input" rows={8} value={form.final_report}
                  onChange={(e) => setForm({ ...form, final_report: e.target.value })}
                  placeholder="Edita, corrige o amplía el borrador. Este texto queda validado con tu nombre." />
      </div>
      <div className="flex gap-3">
        <Button data-testid="exam-save-btn" onClick={save} disabled={saving || !form.date}
                className="rounded-full bg-orange-600 hover:bg-orange-700 text-white">
          {saving ? "Guardando…" : exam ? "Guardar cambios" : "Registrar examen"}
        </Button>
        <Button variant="outline" onClick={onCancel} className="rounded-full">Cancelar</Button>
      </div>
    </div>
  );
}
